import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  SxProps,
} from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { RadioButtonStyled } from '@/components/hook-form/RHFRadioButton';
import Iconify from '@/components/Iconify';
import { ICON_SIZE_MAP } from '@/config';

type Props = {
  name: string;
  options: { value: string | number; label: ReactNode; subtitle?: string; content?: ReactNode }[];
  sx?: SxProps;
  isDisabled?: boolean;
};

export default function RHFRadioButtonsCollapsible({ name, options, sx, isDisabled = false }: Props) {
  const { control } = useFormContext();
  const [expandedOption, setExpandedOption] = useState<string | number | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    // Only auto-expand on initial load, not after user has manually interacted
    const currentValue = control._formValues[name];
    if (currentValue && expandedOption === null && !hasUserInteracted) {
      setExpandedOption(currentValue);
    }
  }, [control._formValues, name, expandedOption, hasUserInteracted]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const toggleExpansion = (optionValue: string | number) => {
          setHasUserInteracted(true);
          setExpandedOption(expandedOption === optionValue ? null : optionValue);
        };

        return (
          <FormControl
            sx={{
              width: '100%',
              ...sx,
            }}
          >
            <Stack spacing={2}>
              {options.map((option) => {
                const isSelected = value === option.value;
                const isExpanded = expandedOption === option.value;

                return (
                  <Accordion
                    key={option.value}
                    expanded={isExpanded}
                    disabled={isDisabled}
                    sx={(theme) => ({
                      border: 'none',
                      '&:before': {
                        display: 'none',
                      },
                      '&.Mui-expanded': {
                        boxShadow: 'none',
                        '&:first-child': {
                          m: 0,
                        },
                      },
                      '.MuiAccordionSummary-content': {
                        m: 0,
                        minHeight: theme.spacing(8), // 64px -> theme.spacing(8)
                        '&.Mui-expanded': {
                          m: 0,
                        },
                      },
                    })}
                    elevation={0}
                  >
                    <AccordionSummary
                      expandIcon={
                        <IconButton
                          disabled={isDisabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            onChange(option.value);
                            toggleExpansion(option.value);
                          }}
                        >
                          <Iconify icon={ICONS.CHEVRON_DOWN} width={ICON_SIZE_MAP.md} height={ICON_SIZE_MAP.md} />
                        </IconButton>
                      }
                      onClick={() => {
                        if (!isDisabled) {
                          onChange(option.value);
                          toggleExpansion(option.value);
                        }
                      }}
                      sx={{
                        border: `1px solid`,
                        borderRadius: 1,
                        '&.Mui-expanded': {
                          borderBottomLeftRadius: 0,
                          borderBottomRightRadius: 0,
                          my: 0,
                        },
                        '&:hover': {
                          backgroundColor: 'hover',
                        },
                        backgroundColor: 'transparent',
                        borderColor: 'divider',
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          transform: 'none',
                          '&.Mui-expanded': {
                            transform: 'none',
                          },
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        sx={{ flex: 1, cursor: isDisabled ? 'default' : 'pointer' }}
                        onClick={
                          isDisabled
                            ? undefined
                            : (event) => {
                                event.stopPropagation();
                                onChange(option.value);
                                toggleExpansion(option.value);
                              }
                        }
                      >
                        <RadioButtonStyled
                          tabIndex={-1}
                          type="radio"
                          checked={isSelected}
                          readOnly
                          sx={{ cursor: isDisabled ? 'default' : 'pointer' }}
                        />
                        <Stack>
                          <PTypography variant="subtitle2">{option.label}</PTypography>
                          {!!option.subtitle && (
                            <PTypography variant="body2" color="disabled">
                              {option.subtitle}
                            </PTypography>
                          )}
                        </Stack>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={(theme) => ({
                        py: theme.spacing(2), // 16px -> theme.spacing(2)
                        border: `1px solid`,
                        borderTop: 'none',
                        borderBottomLeftRadius: theme.shape.borderRadius,
                        borderBottomRightRadius: theme.shape.borderRadius,
                        borderColor: 'divider',
                      })}
                    >
                      {option.content || (
                        <PTypography variant="body2" color="secondary">
                          {option.label}
                        </PTypography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
            {!!error && (
              <FormHelperText error sx={{ mt: 1 }}>
                {error.message}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
